import { useState, useEffect } from 'react';
import { configService } from '@/services/configService';
import { useAuth } from '@/hooks/use-auth';
import { useCompany } from '@/hooks/use-company';

/**
 * Custom hook to build the feedback collector URL with all required parameters.
 * 
 * @param feature - The feature name to collect feedback for (e.g., "Disputes", "Dashboard")
 * @returns The complete URL for the oc-component, or null if config is not loaded yet
 */
export function useFeedbackCollectorUrl(feature: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  const { user } = useAuth();
  const { selectedCompany } = useCompany();

  useEffect(() => {
    let isMounted = true;

    async function buildUrl() {
      try {
        const config = await configService.getFeedbackCollectorConfig();
        
        if (!isMounted) return;

        const params = new URLSearchParams();
        params.set('product', config.product);
        params.set('feature', feature);
        params.set('accountToken', config.accountToken);
        params.set('customerName', selectedCompany?.name || 'Unknown');
        params.set('userId', user?.id || 'Unknown');

        const fullUrl = `${config.baseUrl}?${params.toString()}`;
        setUrl(fullUrl);
      } catch (error) {
        console.error('Failed to build feedback collector URL:', error);
        setUrl(null);
      }
    }

    buildUrl();

    return () => {
      isMounted = false;
    };
  }, [feature, user, selectedCompany]);

  return url;
}
